import { DropdownMenu, DropdownMenuItem, Icon, Select, Switch, TableColumn, Tooltip } from '@aon/aon-tws-common-ui';
import globalVars from '@aon/aon-tws-common-ui/build/lib/globalVars.module.scss';
import { ResponseOptionsTooltip } from '@aon/tws-common-ui-questions';
import * as React from 'react';
import { ReactReduxContext } from 'react-redux';
import styled from 'styled-components';
import customFlag from '../../assets/images/icon-customFlag.png';
import Constants from '../../constants';
import { CustomQuestionEditModalContainer } from '../../containers/questions/CustomQuestionEditModalContainer';
import ContentConstants from '../../contentConstants';
import { ISurveyQuestionEditModel, QuestionType, SeverityType, SourceType, SurveyStep } from '../../models';
import { FormattedTd } from '../common';
import { CustomBanner } from '../common/CustomBanner';
import { WarningBox } from '../common/warnings';
import { QuestionViewUpdateButton } from './QuestionViewUpdateButton';

interface IDropdownToggleProps {
	isOpen: boolean;
}

export interface IQuestionListRowProps {
	disableIsRequired?: boolean;
	errors?: string[];
	includeDimension: boolean;
	isExpanded?: boolean;
	isSummary: boolean;
	isSurveyAdmin?: boolean;
	linkToErrors?: SurveyStep;
	linkToWarnings?: SurveyStep;
	question: ISurveyQuestionEditModel;
	showBranching: boolean;
	showFollowUpColumn: boolean;
	warnings?: string[];
	openModal: (parentQuestion: ISurveyQuestionEditModel) => void;
	openCustomModal: (parentQuestion: ISurveyQuestionEditModel) => void;
	DeleteSurveyQuestion?: (question: ISurveyQuestionEditModel) => void;
	ToggleFollowUpQuestions?: (questionId: number) => void;
	ToggleQuestionIsRequired?: (question: ISurveyQuestionEditModel, isRequired: boolean) => void;
	ToggleDisplayLogic?: () => void;
	UpdateAllQuestionTypes: (questions: ISurveyQuestionEditModel[], questionType: QuestionType) => void;
}

export interface IQuestionListRowState {
	isFollowUpToggleOpen: boolean;
	isModalOpen: boolean;
}

export const ActionColumn = styled(TableColumn).attrs<{}>({
	className: 'tws-table-column',
})`
	white-space: nowrap;
`;
ActionColumn.displayName = 'ActionColumn';

export const DrawerToggle = styled.div.attrs({
	className: 'tws-drawer-toggle',
	role: 'button',
})`
	cursor: pointer;
	height: 30px;
	text-align: center;
	vertical-align: middle;
	width: 30px;
	:hover {
		background: ${globalVars.mainBlue};
		color: ${globalVars.white};
		cursor: pointer;
	}
	.tws-i-icon {
		position: relative;
		top: 5px;
	}
`;
DrawerToggle.displayName = 'DrawerToggle';

export const DropdownToggle = styled.div.attrs({
	className: 'tws-dropdown-toggle',
	role: 'button',
})`
	cursor: pointer;
	height: 30px;
	text-align: center;
	vertical-align: middle;
	width: 30px;
	:hover {
		background: ${globalVars.mainBlue};
		color: ${globalVars.white};
		cursor: pointer;
	}
	.tws-i-icon {
		position: relative;
		top: 5px;
	}
	${(props: IDropdownToggleProps) => (props.isOpen ? 'background: ' + globalVars.mainBlue + '; color: ' + globalVars.white + ';' : '')}
`;
DropdownToggle.displayName = 'DropdownToggle';

const DimensionTextStyling = styled.div`
	font-size: 12px;
	font-weight: 500;
	margin-bottom: 12px;
`;
const QuestionTextStyling = styled.div`
	font-size: 16px;
	font-weight: 600;
	line-height: 1.2em;
`;
export const QuestionCell = styled(FormattedTd)<{ expanded: boolean }>`
	background-color: ${(props) => (props.expanded ? '#f6f6f6' : 'rgb(237, 244, 247)')};
`;
QuestionCell.displayName = 'QuestionCell';

export const RemoveIcon = styled.div`
	cursor: pointer;
	float: right;
	height: 30px;
	text-align: center;
	vertical-align: middle;
	width: 30px;

	.tws-i-icon {
		position: relative;
		top: 5px;
	}
`;

const LastColumn = styled.div`
	text-align: right;
	margin-right: 10px;
`;
const BranchContainer = styled.div`
	cursor: pointer;
	background-color: ${globalVars.mainBlue};
	color: white;
	width: 36px;
	text-align: center;
	float: right;
	border-radius: 2px;
	height: 25px;
	padding: 3px;
	display: inline-flex;
`;
BranchContainer.displayName = 'BranchContainer';
const ArrowContainer = styled.div`
	margin-left: 5px;
	font-size: 8px;
	display: inline-block;
	margin-top: 4px;
`;
const BranchIconContainer = styled.div`
	display: inline-block;
	margin-left: 2px;
`;
const NoBranchIconContainer = styled.div`
	margin-right: 15px;
`;
NoBranchIconContainer.displayName = 'NoBranchIconContainer';

// This component is used on Confirm and Schedule page. QuestionDesignRow component is used on Logic and Layout page.
export class QuestionRow extends React.Component<IQuestionListRowProps, IQuestionListRowState> {
	public constructor(props: IQuestionListRowProps) {
		super(props);

		this.state = { isFollowUpToggleOpen: false, isModalOpen: false };

		this.getQuestionBranchingColumn = this.getQuestionBranchingColumn.bind(this);
		this.handleDrawerToggle = this.handleDrawerToggle.bind(this);
		this.handleDropdownToggle = this.handleDropdownToggle.bind(this);
		this.handleIsRequiredToggle = this.handleIsRequiredToggle.bind(this);
		this.handleOnClick = this.handleOnClick.bind(this);
		this.handleOpenModal = this.handleOpenModal.bind(this);
		this.handleOpenCustomModal = this.handleOpenCustomModal.bind(this);
		this.toggleCustomQuestionModal = this.toggleCustomQuestionModal.bind(this);
	}

	public render() {
		const { isSummary, question } = this.props;
		const isExpandedRow = question.parentQuestionId !== null && question.parentQuestionId !== undefined;
		return (
			<>
				{isSummary && this.getDisplayOrderColumn(isExpandedRow)}
				{this.getQuestionTextColumn(isExpandedRow)}
				{this.getResponseOptionGroupColumn(isExpandedRow)}
				{this.getQuestionTypeColumn(isExpandedRow)}
				{isSummary && this.getQuestionBranchingColumn(isExpandedRow)}
				{!isSummary && (
					<>
						{this.getResponseRequiredColumn(isExpandedRow)}
						{this.getFollowUpColumn(isExpandedRow)}
						{this.getActionColumn(isExpandedRow)}
					</>
				)}
			</>
		);
	}

	private getDisplayOrderColumn(isExpandedRow: boolean) {
		const { question } = this.props;

		return (
			<QuestionCell flexBasis={0} expanded={isExpandedRow} className='tws-summary-question-sequence'>
				{question.displayOrder}
			</QuestionCell>
		);
	}

	private handleDimensionName() {
		const { dimensionNames } = this.props.question;
		if (!dimensionNames || !this.props.includeDimension) {
			return '';
		}

		const orderedDimensionNames = [...dimensionNames].sort((a, b) => a.localeCompare(b));
		return <DimensionTextStyling>{orderedDimensionNames.join(', ')}</DimensionTextStyling>;
	}

	private handleOnClick() {
		const { DeleteSurveyQuestion, question } = this.props;
		if (DeleteSurveyQuestion) {
			DeleteSurveyQuestion(question);
		}
	}

	private getQuestionBranchingColumn(isExpandedRow: boolean) {
		const { showBranching } = this.props;
		const angle = showBranching ? 'angle-up' : 'angle-down';

		return (
			<QuestionCell flexBasis={13} expanded={isExpandedRow}>
				<LastColumn>
					{this.props.question.displayRulesCount > 0 ? (
						<BranchContainer onClick={this.props.ToggleDisplayLogic}>
							<BranchIconContainer>{this.props.question.displayRulesCount}</BranchIconContainer>
							<ArrowContainer>
								<Icon name={angle} />
							</ArrowContainer>
						</BranchContainer>
					) : (
						<NoBranchIconContainer>--</NoBranchIconContainer>
					)}
				</LastColumn>
			</QuestionCell>
		);
	}

	private getQuestionTextColumn(isExpandedRow: boolean) {
		const { question, warnings, linkToWarnings, errors, linkToErrors } = this.props;
		return (
			<QuestionCell flexBasis={40} expanded={isExpandedRow}>
				{this.handleDimensionName()}
				<QuestionTextStyling>{question.text}</QuestionTextStyling>
				{this.getViewUpdateButton()}
				{question.sourceType === SourceType.Custom && <CustomBanner src={customFlag} />}
				{warnings && warnings.length > 0 && <WarningBox severity={SeverityType.Warning} messages={warnings} linkToStep={linkToWarnings} />}
				{errors && errors.length > 0 && <WarningBox severity={SeverityType.Error} messages={errors} linkToStep={linkToErrors} />}
			</QuestionCell>
		);
	}

	private getResponseOptionGroupColumn(isExpandedRow: boolean) {
		const { question } = this.props;
		const tooltipId = question.sourceType === SourceType.Custom ? question.surveyQuestionId : question.sourceQuestionVariationId;
		const responseOptionGroup =
			question.responseOptionGroup && question.responseOptionGroup.length > 0
				? question.responseOptionGroup
				: ContentConstants.EMPTY_PLACEHOLDER;
		if (isExpandedRow) {
			return (
				<QuestionCell flexBasis={16} expanded={true}>
					<span>{responseOptionGroup}</span>
					<Tooltip id={tooltipId + '_' + responseOptionGroup} contentType='jsx'>
						{ContentConstants.FOLLOW_UP_QUESTION_RESPONSE_OPTION_GROUP_TOOLTIP}
					</Tooltip>
				</QuestionCell>
			);
		}
		return (
			<QuestionCell flexBasis={16} expanded={false}>
				<ResponseOptionsTooltip
					id={tooltipId}
					questionType={question.questionType}
					responseOptionGroup={question.responseOptionGroup}
					responseOptions={question.responseOptions}
				/>
			</QuestionCell>
		);
	}

	private getQuestionTypeColumn(isExpandedRow: boolean) {
		const { isSummary, question, UpdateAllQuestionTypes } = this.props;
		if (isSummary) {
			return (
				<QuestionCell flexBasis={16} expanded={isExpandedRow}>
					{this.getQuestionTypeColumnText(question)}
				</QuestionCell>
			);
		}
		if (question.isDemographic) {
			return <></>;
		}

		return (
			<QuestionCell flexBasis={23} expanded={isExpandedRow}>
				{(question.questionType === QuestionType.Scale || question.questionType === QuestionType.Slider) && (
					<Select
						items={Constants.QuestionTypeOptions}
						displayText='Select'
						onApply={(value) => UpdateAllQuestionTypes([question], value as unknown as QuestionType)}
						selectedValue={question.questionType.toString()}
					/>
				)}
				{(question.questionType === QuestionType.TextBox || question.questionType === QuestionType.OpenEnded) && (
					<>{question.responseOptionGroup || ContentConstants.EMPTY_PLACEHOLDER}</>
				)}
				{(question.questionType === QuestionType.Checkbox ||
					question.questionType === QuestionType.Ranking ||
					question.questionType === QuestionType.Hierarchy) && <>{QuestionType[question.questionType]}</>}
			</QuestionCell>
		);
	}

	private getResponseRequiredColumn(isExpandedRow: boolean) {
		const { disableIsRequired, question } = this.props;
		return (
			<QuestionCell flexBasis={13} expanded={isExpandedRow}>
				<Switch checked={question.isRequired} disabled={disableIsRequired} getValue={this.handleIsRequiredToggle as any} />
			</QuestionCell>
		);
	}
	private getFollowUpColumn(isExpandedRow: boolean) {
		const { isExpanded, question, showFollowUpColumn } = this.props;
		// Exit early because only open ended questions get a follow-up icon.
		if (!showFollowUpColumn) {
			return;
		}
		if (showFollowUpColumn && question.questionType !== QuestionType.OpenEnded) {
			return (
				<QuestionCell flexBasis={8} expanded={isExpandedRow}>
					{<></>}
				</QuestionCell>
			);
		}

		return (
			<QuestionCell flexBasis={8} expanded={isExpandedRow}>
				{isExpanded === undefined ? this.getAddIcon(question) : this.getDrawerIcon(isExpanded)}
			</QuestionCell>
		);
	}

	private getAddIcon(question: ISurveyQuestionEditModel) {
		return (
			<div className='position-relative'>
				<DropdownToggle aria-label='Add Follow-Up Question' onClick={this.handleDropdownToggle} isOpen={this.state.isFollowUpToggleOpen}>
					<Icon name='add' />
				</DropdownToggle>
				<DropdownMenu onBlur={this.handleDropdownToggle} open={this.state.isFollowUpToggleOpen} x={-170} y={0}>
					<DropdownMenuItem onClick={() => this.handleOpenModal(question)}>
						{ContentConstants.FOLLOW_UP_QUESTION_DROPDOWN_STANDARD_QUESTION}
					</DropdownMenuItem>
					<DropdownMenuItem onClick={() => this.handleOpenCustomModal(question)}>
						{ContentConstants.FOLLOW_UP_QUESTION_DROPDOWN_CUSTOM_QUESTION}
					</DropdownMenuItem>
				</DropdownMenu>
			</div>
		);
	}

	private getDrawerIcon(isExpanded: boolean) {
		const { question } = this.props;
		return (
			<DrawerToggle aria-label='Toggle Follow-Up Question Drawer' onClick={() => this.handleDrawerToggle(question.id)}>
				<Icon name={isExpanded ? 'angle-up' : 'angle-down'} />
			</DrawerToggle>
		);
	}

	private handleIsRequiredToggle(value: boolean) {
		if (this.props.ToggleQuestionIsRequired) {
			this.props.ToggleQuestionIsRequired(this.props.question, value);
		}
	}

	private getActionColumn(isExpandedRow: boolean) {
		const { question, DeleteSurveyQuestion } = this.props;
		const isCustom = question.sourceType === SourceType.Custom;
		return (
			<QuestionCell flexBasis={8} expanded={isExpandedRow} rightAlign={DeleteSurveyQuestion !== undefined}>
				<ActionColumn>
					<RemoveIcon onClick={this.handleOnClick}>
						<Icon name='close' data-testid='delete-question' />
					</RemoveIcon>
					{isCustom && (
						<>
							<RemoveIcon onClick={() => this.toggleCustomQuestionModal(true)}>
								<Icon name='edit' />
							</RemoveIcon>
							{/* The multiple levels of the component structure (container -> component -> container -> component etc)
								is not working when we have upgraded react-redux to latest version 7.2.1.
								The connect function in the containers is not able to pass the existing redux context
								from parent to child components through all levels.
								So we are explicitly getting the store and passing to the container as prop.
								ToDo: We will need to explore if there is a different way to fix this issue*/}
							<ReactReduxContext.Consumer>
								{({ store }) => {
									return (
										<CustomQuestionEditModalContainer
											store={store}
											question={this.props.question}
											isOpen={this.state.isModalOpen}
											ToggleModal={this.toggleCustomQuestionModal}
										/>
									);
								}}
							</ReactReduxContext.Consumer>
						</>
					)}
				</ActionColumn>
			</QuestionCell>
		);
	}

	private getViewUpdateButton = () => {
		const { isSummary, isSurveyAdmin, question } = this.props;
		return (
			<>{question.hasUpdates && <QuestionViewUpdateButton isSummary={isSummary} isSurveyAdmin={isSurveyAdmin} question={question} />}</>
		);
	};

	private toggleCustomQuestionModal(isOpen: boolean) {
		this.setState({ isModalOpen: isOpen });
	}

	private getQuestionTypeColumnText(question: ISurveyQuestionEditModel) {
		switch (question.questionType) {
			case QuestionType.Scale:
				return ContentConstants.QUESTION_TYPE_RADIO_BUTTON;
			case QuestionType.Slider:
			case QuestionType.Checkbox:
			case QuestionType.Ranking:
			case QuestionType.Hierarchy:
				return QuestionType[question.questionType];
			default:
				return question.responseOptionGroup || ContentConstants.EMPTY_PLACEHOLDER;
		}
	}

	private handleDrawerToggle(questionId: number) {
		if (this.props.ToggleFollowUpQuestions) {
			this.props.ToggleFollowUpQuestions(questionId);
		}
	}

	private handleDropdownToggle() {
		this.setState({ isFollowUpToggleOpen: !this.state.isFollowUpToggleOpen });
	}
	private handleOpenModal(parentQuestion: ISurveyQuestionEditModel) {
		// Close dropdown when opening the modal - Modal dropdowns won't behave correctly otherwise.
		this.handleDropdownToggle();
		this.props.openModal(parentQuestion);
	}
	private handleOpenCustomModal(parentQuestion: ISurveyQuestionEditModel) {
		// Close dropdown when opening the modal - Modal dropdowns won't behave correctly otherwise.
		this.handleDropdownToggle();
		this.props.openCustomModal(parentQuestion);
	}
}
